import React from 'react';
import { InfoBadge } from '@medely/ui-kit';
import { ICondition } from '@medely/types';
import { ConditionSatisfactionHelper } from '@medely/credentials-tools';
import {
  useProfessionalQualifications,
  useCurrentUser,
  useNearFutureExpiringConditions,
} from '@mp';
import moment from 'moment';

interface MissingConditionListItemBadgeProps {
  condition: ICondition;
}

export const MissingConditionListItemBadge = ({
  condition,
  ...props
}: MissingConditionListItemBadgeProps) => {
  const { currentUser } = useCurrentUser();
  const { allProfessionalQualifications } = useProfessionalQualifications();
  const { expiringConditions } = useNearFutureExpiringConditions();
  const conditionHelper = new ConditionSatisfactionHelper({
    condition,
    professionalQualifications: allProfessionalQualifications,
    satisfiedConditionIds: currentUser?.professional?.satisfied_condition_ids ?? [],
    expiringSoonConditionIds: expiringConditions.map((condition) => condition.id),
  });

  const isExpiringSoon = expiringConditions.some(
    (expiringCondition) => expiringCondition.id === condition.id,
  );
  const conditionQualificationsIds = condition.qualifications.map(
    (qualification) => qualification.id,
  );
  const professionalQualifications = allProfessionalQualifications.filter((qualification) =>
    conditionQualificationsIds.includes(qualification.qualification.id),
  );

  if (conditionHelper.isAwaitingReviewCheckExpiringSoon) {
    return <InfoBadge label="In review" color="info" {...props} />;
  }

  if (isExpiringSoon) {
    const date = professionalQualifications[0]?.professional_qualification_attributes?.find(
      (attribute) => attribute.qualification_attribute?.field_type === 'expiration_date',
    )?.value;
    const expirationDate = date ? moment(date).format('M/D/YY') : 'soon';
    return <InfoBadge label={`Expires ${expirationDate}`} color="warning" {...props} />;
  }

  if (conditionHelper.unsatisfiedDueToRejectedPq) {
    return <InfoBadge label="Needs attention" color="error" {...props} />;
  }

  if (conditionHelper.unsatisfiedDueToExpiredPq) {
    return <InfoBadge label="Expired" color="error" {...props} />;
  }

  if (conditionHelper.isSatisfied) {
    return <InfoBadge label="Completed" color="success" {...props} />;
  }

  if (professionalQualifications.some((pq) => ['rejected', 'expired'].includes(pq.status))) {
    return <InfoBadge label="Needs attention" color="error" {...props} />;
  }

  return <InfoBadge label="Missing" color="error" {...props} />;
};
